import { Routes, Route } from 'react-router-dom'
import { Layout } from '@/components/layout'
import { AdminLayout } from '@/components/admin'
import {
  HomePage,
  AboutPage,
  ContactPage,
  ProductsPage,
  CategoryPage,
  ProductDetailPage,
  GalleryPage,
  CertificatesPage,
  QuoteBasketPage,
  ComingSoonPage,
} from '@/pages/public'
import {
  LoginPage,
  DashboardPage,
  ProductsListPage,
  ProductFormPage,
  CategoriesPage,
  QuotesPage,
  GalleryPage as AdminGalleryPage,
  CertificatesPage as AdminCertificatesPage,
  SubscribersPage,
  MessagesPage,
  SettingsPage,
  ConfiguratorTemplatesPage,
  FilterDefinitionsPage,
  SEOPage,
  LeadsPage,
  LeadDetailPage,
  LeadStatisticsPage,
  ShortlistPage,
  FactoryInfoPage,
} from '@/pages/admin'
import { useMaintenanceMode, isPreviewMode } from '@/hooks/useSiteSettings'

// Wrapper component for public routes that checks maintenance mode
function PublicRouteWrapper() {
  const { data: maintenanceMode, isLoading } = useMaintenanceMode()
  const preview = isPreviewMode()

  // Show loading state briefly
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    )
  }

  // If maintenance mode is enabled and not in preview mode, show coming soon
  if (maintenanceMode?.enabled && !preview) {
    return <ComingSoonPage />
  }

  // Otherwise, show normal layout with child routes
  return <Layout />
}

function App() {
  return (
    <Routes>
      {/* Public Routes - wrapped with maintenance mode check */}
      <Route element={<PublicRouteWrapper />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:category" element={<CategoryPage />} />
        <Route path="/products/:category/:product" element={<ProductDetailPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/certificates" element={<CertificatesPage />} />
        <Route path="/quote-basket" element={<QuoteBasketPage />} />
      </Route>

      {/* Admin Routes - always accessible */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="products" element={<ProductsListPage />} />
        <Route path="products/new" element={<ProductFormPage />} />
        <Route path="products/:id" element={<ProductFormPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="filters" element={<FilterDefinitionsPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="gallery" element={<AdminGalleryPage />} />
        <Route path="certificates" element={<AdminCertificatesPage />} />
        <Route path="subscribers" element={<SubscribersPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="factory-info" element={<FactoryInfoPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="seo" element={<SEOPage />} />
        <Route path="configurator-templates" element={<ConfiguratorTemplatesPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:id" element={<LeadDetailPage />} />
        <Route path="shortlist" element={<ShortlistPage />} />
        <Route path="lead-stats" element={<LeadStatisticsPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-secondary-900 mb-4">404</h1>
        <p className="text-secondary-600 mb-8">Page not found</p>
        <a href="/" className="btn-primary px-6 py-3">
          Go Home
        </a>
      </div>
    </div>
  )
}

export default App
